export interface DialogPopupWindowGeometry {
  width: number;
  height: number;
  left: number;
  top: number;
  features: string;
}

interface DialogPopupWindowOptions {
  widthRatio?: number;
  heightRatio?: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  horizontalMargin?: number;
  verticalMargin?: number;
}

const DEFAULT_POPUP_OPTIONS = {
  widthRatio: 0.76,
  heightRatio: 0.82,
  minWidth: 980,
  minHeight: 720,
  maxWidth: 1680,
  maxHeight: 1040,
  horizontalMargin: 24,
  verticalMargin: 48,
} satisfies Required<DialogPopupWindowOptions>;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function buildCenteredDialogPopupWindow(
  options: DialogPopupWindowOptions = {}
): DialogPopupWindowGeometry {
  const resolved = { ...DEFAULT_POPUP_OPTIONS, ...options };
  const screenInfo = window.screen as Screen & {
    availLeft?: number;
    availTop?: number;
  };
  const availableLeft = screenInfo.availLeft ?? 0;
  const availableTop = screenInfo.availTop ?? 0;
  const availableWidth = screenInfo.availWidth || window.innerWidth || resolved.maxWidth;
  const availableHeight = screenInfo.availHeight || window.innerHeight || resolved.maxHeight;
  const boundedAvailableWidth = Math.max(320, availableWidth - resolved.horizontalMargin);
  const boundedAvailableHeight = Math.max(320, availableHeight - resolved.verticalMargin);
  const targetWidth = Math.round(availableWidth * resolved.widthRatio);
  const targetHeight = Math.round(availableHeight * resolved.heightRatio);
  const width = Math.min(
    boundedAvailableWidth,
    clamp(targetWidth, resolved.minWidth, resolved.maxWidth)
  );
  const height = Math.min(
    boundedAvailableHeight,
    clamp(targetHeight, resolved.minHeight, resolved.maxHeight)
  );
  const left = Math.max(0, availableLeft + Math.round((availableWidth - width) / 2));
  const top = Math.max(0, availableTop + Math.round((availableHeight - height) / 2));
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    `screenX=${left}`,
    `screenY=${top}`,
    "menubar=no",
    "toolbar=no",
    "location=no",
    "status=no",
    "resizable=yes",
    "scrollbars=yes",
  ].join(",");

  return {
    width,
    height,
    left,
    top,
    features,
  };
}

export function applyDialogPopupWindowGeometry(
  popup: Window,
  geometry: DialogPopupWindowGeometry
): void {
  try {
    popup.resizeTo(geometry.width, geometry.height);
    popup.moveTo(geometry.left, geometry.top);
  } catch {
    // Some hosts ignore popup resize/move requests after open.
  }
}

export function focusDialogPopupWindow(popup: Window): void {
  try {
    popup.focus();
  } catch {
    // Some hosts block programmatic focus after opening.
  }
}
